import { Label } from "@loot-os/ui/components/label";
import { Switch } from "@loot-os/ui/components/switch";
import { FlaskConical } from "lucide-react";
import type { ReactNode } from "react";

/**
 * Chrome shared by every storefront section page: the sandbox marker so an
 * edit is never mistaken for the live store. Sections differ only in their
 * form, so this keeps that the only thing each page has to say. Loading and
 * failure are the layout's (`StorefrontLayout`); saves and failed saves are
 * toasts (useStorefrontConfig, the query client), not banners. Shipping, a page
 * of its own beside the hub, wears the same chrome and loads the config itself.
 */
export function StorefrontSection({
  title,
  description,
  environment,
  aside,
  children,
}: {
  title: string;
  description: string;
  environment: { name: string; type: string };
  /**
   * Rendered on the title's row, right-aligned: a `SectionSwitch`, for a page that owns one, or a
   * control over the whole page (a preview toggle).
   */
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            {/* No "Storefront /" prefix here — the header breadcrumb owns the trail. */}
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            {environment.type === "sandbox" && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/50 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                <FlaskConical className="size-3" />
                {environment.name}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
        {aside && <div className="shrink-0 pt-1">{aside}</div>}
      </div>

      {children}
    </div>
  );
}

/**
 * The one switch a section owns, on its header row (`aside`): the label, then the switch, saving
 * on flip. The label names the thing switched (Storefront, Balance purchases, Battles, Support
 * chat) and never changes while a save is pending, so the row keeps its width; the disabled
 * switch is the pending signal.
 */
export function SectionSwitch({
  id,
  label,
  checked,
  disabled,
  onCheckedChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <Label htmlFor={id} className="text-sm">
        {label}
      </Label>
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
    </div>
  );
}
